/**
 * PATH: apps/quote/src/components/builder/panels/inspector/ApplicationTab.tsx
 * PURPOSE: Application selector tab for the row inspector
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Package, Loader2, CheckCircle2 } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { quoteApi, type QuoteLineItem } from '@/lib/quoteApi'

interface ApplicationTabProps {
  quoteId: string
  sectionId: string
  lineItem: QuoteLineItem
  onApply: (applicationId: string) => void
  isApplying: boolean
}

export default function ApplicationTab({
  quoteId,
  sectionId,
  lineItem,
  onApply,
  isApplying,
}: ApplicationTabProps) {
  const [search, setSearch] = useState('')

  const { data: applications = [], isLoading } = useQuery({
    queryKey: ['applications', search],
    queryFn: () => quoteApi.listApplications({ search }),
    staleTime: 60 * 1000,
  })

  return (
    <div className="p-3 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-0.5">
          Apply Product Application
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Selecting an application will populate materials and recalculate costs
        </p>
      </div>

      {lineItem.application_id && (
        <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
              Current: {lineItem.product_system || 'Application Applied'}
            </span>
          </div>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search applications..."
          className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-6 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
            <Package className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-1" />
            <p className="text-sm text-slate-500 dark:text-slate-400">No applications found</p>
          </div>
        ) : (
          applications.map((app) => (
            <div
              key={app.id}
              className={cn(
                'p-2.5 rounded-lg border transition-colors cursor-pointer',
                lineItem.application_id === app.id
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700'
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {app.menu_item || app.product_name}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {app.supplier_name} • {app.application_situation}
                  </p>
                  <div className="flex gap-3 mt-1.5 text-xs text-slate-600 dark:text-slate-300">
                    <span>Cost: {formatCurrency(app.total_cost_per_m2 || 0)}/m²</span>
                    <span>Prod: {app.productivity_standard} m²/d</span>
                  </div>
                </div>
                <button
                  onClick={() => onApply(app.id)}
                  disabled={isApplying || lineItem.application_id === app.id}
                  className={cn(
                    'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                    lineItem.application_id === app.id
                      ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 cursor-default'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50'
                  )}
                >
                  {isApplying ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : lineItem.application_id === app.id ? (
                    'Applied'
                  ) : (
                    'Apply'
                  )}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
